// pages/AllRoles.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Shield, Trash2, Edit3, Loader2, Users, AlertCircle, Eye } from 'lucide-react';

const API_BASE = 'https://api.mobilink.tech/api/superAdmin';
const API_TOKEN = sessionStorage.getItem('adminToken') ||
    sessionStorage.getItem('subAdminToken') || '';
const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${API_TOKEN}` };

export const AllRoles = () => {
    const navigate = useNavigate();
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const fetchRoles = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/roles/getall-roles`, { method: 'GET', headers });
            if (!res.ok) throw new Error(`Failed to fetch roles (${res.status})`);
            const data = await res.json();
            setRoles(data.roles || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchRoles(); }, []);

    const handleDelete = async (id, roleName) => {
        if (!window.confirm(`Delete role "${roleName}"? This cannot be undone.`)) return;
        setDeletingId(id);
        try {
            const res = await fetch(`${API_BASE}/roles/deleterole/${id}`, { method: 'DELETE', headers });
            if (!res.ok) throw new Error('Delete failed');
            setRoles(prev => prev.filter(r => r._id !== id));
        } catch (err) {
            alert(`Failed to delete: ${err.message}`);
        } finally {
            setDeletingId(null);
        }
    };

    const filteredRoles = roles.filter(r =>
        r.roleName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.status?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const getPermissionCount = (perms) => {
        if (!perms || (Array.isArray(perms) && perms.length === 0)) return 0;
        const obj = Array.isArray(perms) ? perms[0] : perms;
        if (!obj) return 0;
        let count = 0;
        const walk = (node) => {
            if (node.Actions) count += node.Actions.length;
            if (node.Sections) node.Sections.forEach(walk);
        };
        Object.values(obj).forEach(walk);
        return count;
    };

    return (
        <div className="space-y-6 bg-gray-50 min-h-screen p-4 rounded-2xl">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 sm:p-5 rounded-2xl border border-orange-200 shadow-sm">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                        <Shield size={20} style={{ color: '#FF7D38' }} />
                    </div>
                    <div>
                        <h1 className="text-lg sm:text-xl font-bold text-gray-800">Role Management</h1>
                        <p className="text-xs sm:text-sm text-gray-500">{roles.length} role{roles.length !== 1 ? 's' : ''} configured</p>
                    </div>
                </div>
                <button onClick={() => navigate('/admin/add-roles')}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:shadow-orange-500/30 shadow-sm transition-all whitespace-nowrap">
                    <Plus size={16} /> Add New Role
                </button>
            </div>

            {/* Search */}
            <div className="bg-white p-4 rounded-2xl border border-orange-200 shadow-sm">
                <div className="relative max-w-md">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                        placeholder="Search roles by name or status..."
                        className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl bg-orange-50 border border-orange-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none" />
                </div>
            </div>

            {/* Error State */}
            {error && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-3">
                    <AlertCircle size={18} className="text-red-500 flex-shrink-0" />
                    <p className="text-sm text-red-700">{error}</p>
                    <button onClick={fetchRoles} className="ml-auto text-xs font-medium text-red-600 hover:underline">Retry</button>
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="flex flex-col items-center justify-center py-20">
                    <Loader2 size={36} className="animate-spin text-orange-500 mb-3" />
                    <p className="text-sm text-gray-500">Loading roles...</p>
                </div>
            )}

            {/* Empty State */}
            {!loading && !error && filteredRoles.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-orange-200">
                    <Users size={48} className="text-gray-300 mb-4" />
                    <p className="text-gray-500 font-medium">{searchTerm ? 'No roles match your search' : 'No roles found'}</p>
                    {!searchTerm && (
                        <button onClick={() => navigate('/admin/add-roles')} className="mt-4 text-sm text-orange-500 font-medium hover:underline">Create your first role →</button>
                    )}
                </div>
            )}

            {/* Roles Grid */}
            {!loading && !error && filteredRoles.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filteredRoles.map(role => {
                        const permCount = getPermissionCount(role.rolesAndPermission);
                        const isDeleting = deletingId === role._id;
                        return (
                            <div key={role._id} className="bg-white rounded-2xl border border-orange-200 p-5 hover:shadow-md transition-shadow flex flex-col gap-4">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                                            <Shield size={18} style={{ color: '#FF7D38' }} />
                                        </div>
                                        <div className="min-w-0">
                                            <h3 className="text-sm font-bold text-gray-800 truncate">{role.roleName}</h3>
                                            <p className="text-[10px] text-gray-400 truncate">ID: {role._id.slice(-8)}</p>
                                        </div>
                                    </div>
                                    <span className={`flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full border font-medium ${
                                        role.status === 'active'
                                            ? 'bg-green-50 text-green-600 border-green-200'
                                            : 'bg-gray-50 text-gray-500 border-gray-200'
                                    }`}>{role.status}</span>
                                </div>

                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                    <div className="flex items-center gap-1.5">
                                        <Users size={12} />
                                        <span>{permCount} permission{permCount !== 1 ? 's' : ''}</span>
                                    </div>
                                    <span className="text-gray-300">|</span>
                                    <span>Created {formatDate(role.createdAt)}</span>
                                </div>

                                <div className="flex items-center gap-2 pt-3 border-t border-orange-100 mt-auto">
                                    <button onClick={() => navigate(`/admin/roles/edit/${role._id}`)}
                                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100 transition-colors">
                                        <Edit3 size={13} /> Edit
                                    </button>
                                    <button onClick={() => navigate(`/admin/roles/details/${role._id}`)}
                                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 transition-colors">
                                        <Eye size={13} /> View
                                    </button>
                                    <button onClick={() => handleDelete(role._id, role.roleName)} disabled={isDeleting}
                                        className="flex items-center justify-center w-9 h-9 rounded-lg bg-red-50 text-red-500 border border-red-200 hover:bg-red-100 transition-colors disabled:opacity-50 flex-shrink-0">
                                        {isDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default AllRoles;